import { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { usePreferencesStore } from '../store/preferencesStore';
import Ghost from './auth/Ghost.jsx';
import AiKeySetupState from './aiChat/AiKeySetupState';
import AiFallbackNotice from './aiChat/AiFallbackNotice';
import AiDisclosureLine from './aiChat/AiDisclosureLine';
import AiStarterPrompts from './aiChat/AiStarterPrompts';
import { clearConversation } from '../services/ai/conversationStore';
import { readCachedConversation, clearCachedConversation, persistToken } from '../services/ai/conversationCache';
import { setSettingsModalOpen, setSettingsInitialTab } from '../store/uiModalStore';
import { openVaultGate } from '../store/vaultStore';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import { buildWhatIsSentText } from './aiChat/whatIsSentText';
import { resolveShortcutKeys, shortcutLabelString } from '../config/shortcuts';
import { recordFeature, ACTION } from '../services/telemetry';
import { projectContextLine } from '../services/ai/aiProjectScope';
import {
  AI_PANEL_AUTORUN_KEY,
  AI_PANEL_INTERACTIVE_KEY,
  AI_PANEL_MIN_WIDTH,
  CONFIRM_PENDING,
} from './aiChat/aiChatConstants';
import {
  INTERACTIVE_UI_TOOLS,
  TERMINAL_TOOLS,
  resolveChainedToolInput,
  toolNeedsConfirm,
  updateChainContext,
} from './aiChat/toolRegistry';
import { buildToolTurnHistoryEntry, condenseToolResultForApi } from './aiChat/condenseToolResult';
import { buildReferenceContext } from './aiChat/chatInputHelpers';
import { clampPanelWidth } from './aiChat/panelWidth';
import { MessageBubble } from './aiChat/MessageBubble';
import { ChatInput } from './aiChat/ChatInput';
import { useAiReadiness } from './aiChat/useAiReadiness';
import { useAiPanelResize } from './aiChat/useAiPanelResize';
import { useAiToolExecutor } from './aiChat/useAiToolExecutor';
import { useAiStreamTurn } from './aiChat/useAiStreamTurn';
import { useConversationPersistence } from './aiChat/useConversationPersistence';
import { AiChatHeader } from './aiChat/AiChatHeader';
import { AiChatStatusBar } from './aiChat/AiChatStatusBar';

// ── Main component ─────────────────────────────────────────────────────────────

function AIChatPanel({
  isOpen,
  onClose,
  collections,
  selectedRequest,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  environments,
  currentWorkspaceId,
  onExecuteRequest,
  getCurrentEditorRequest,
  onRunCollection,
  onSetVariable,
  onCreateCollection,
  onCreateFolders,
  onCreateRequest,
  onMoveRequests,
  onGetLastResponse,
  onGenerateTests,
  onGeneratePreRequestScript,
  onUpdateRequest,
  getCollections,
  extensionActions,
}) {
  // Seeded from the in-memory cache so a reopened panel paints its thread on
  // the first frame (App unmounts the panel on close — see the persistence
  // effects below).
  const [messages, setMessages] = useState(() => readCachedConversation(currentWorkspaceId)?.messages || []);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  // Live context size: total tokens in the last prompt sent to the model
  // (input + cache read + cache creation from the most recent `usage` event).
  // Reflects how large the current conversation has grown.
  const [contextTokens, setContextTokens] = useState(0);
  // Set when a turn fails: { payload, history } — the exact request to replay.
  const [retryable, setRetryable] = useState(null);
  const [interactiveMode, setInteractiveMode] = useState(() => {
    try {
      return localStorage.getItem(AI_PANEL_INTERACTIVE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  // Auto-run: when ON the assistant executes side-effecting actions immediately
  // and chains multi-step work (agentic). When OFF it pauses for a Confirm/Cancel
  // before each request/SQL/task action. Read live in the loop via a ref.
  const [autoRunMode, setAutoRunMode] = useState(() => {
    try {
      return localStorage.getItem(AI_PANEL_AUTORUN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const autoRunModeRef = useRef(autoRunMode);
  useEffect(() => { autoRunModeRef.current = autoRunMode; }, [autoRunMode]);
  const { t } = useTranslation();
  // "Let AI read responses" — the response-privacy gate (default OFF). A
  // persisted preference, not panel-local state, so Settings → AI chat shows the
  // same switch and the gate reads it live (services/ai/responseAccess.js).
  const aiReadResponses = usePreferencesStore((s) => s.aiReadResponses === true);
  // The close tooltip names the REAL toggle binding (remappable; ⌘ on a Mac) — card 50122def.
  const keyboardShortcuts = usePreferencesStore((s) => s.keyboardShortcuts);
  const closeLabel = `Close (${shortcutLabelString(resolveShortcutKeys('toggleAiPanel', keyboardShortcuts))})`;
  const updatePreference = usePreferencesStore((s) => s.updatePreference);
  // Resolver for the currently-pending Confirm/Cancel gate (one at a time,
  // since tools execute sequentially). Mirrors collectionRunnerAiResolveRef.
  const confirmResolveRef = useRef(null);
  // Set while a stream turn is in flight; calling it resolves that turn early
  // (used by the Stop button to interrupt generation immediately).
  const streamFinishRef = useRef(null);

  const messagesContainerRef = useRef(null);
  const abortRef = useRef(false);
  // P2: allow exactly one auto-diagnosis turn per user message when a run fails,
  // even with Auto-run off. Reset at the start of each send.
  const runDiagnosisUsedRef = useRef(false);
  // fetch_url calls in the current user turn (NB2). Reset in sendMessage.
  const fetchUrlTurnCountRef = useRef(0);
  const shouldAutoScrollRef = useRef(true);
  // Anthropic-format message history for the API
  const anthropicHistoryRef = useRef([]);
  // The in-flight turn's message list (settled turns + this user turn + tool
  // rounds that completed) — what a mid-turn close may persist. Never ends in
  // a bare tool_use: it is advanced only at points where the pairs are whole.
  const inFlightHistoryRef = useRef(null);
  const threadRef = useRef({ workspaceId: currentWorkspaceId, token: persistToken() });

  // BYO key: there is no per-user server transport to tear down anymore
  // (the Phoenix AI socket died with the proxy), but a user change is still
  // an account handoff — drop the stale context count.
  // (Don't reset on every isOpen toggle — an open conversation persists.)
  const currentUserId = useAuthStore((s) => s.user?.id);
  useEffect(() => {
    setContextTokens(0);
  }, [currentUserId]);

  const aiReadiness = useAiReadiness(isOpen);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 80;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    const el = messagesContainerRef.current;
    if (!el || (!force && !shouldAutoScrollRef.current)) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  const { panelWidth, isResizing, handleResizeStart } = useAiPanelResize();

  useEffect(() => {
    // Force scroll to bottom while AI is generating; respect user scroll otherwise.
    scrollToBottom(isStreaming);
  }, [messages, isStreaming, scrollToBottom]);

  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (confirmResolveRef.current) {
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        resolve(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- holds the in-flight turn's resolver (set by useAiStreamTurn), not a DOM node: read it at cleanup time
      streamFinishRef.current?.();
    };
  }, []);

  const executeTool = useAiToolExecutor({
    collections,
    environments,
    workspaceVariables,
    globalVariables,
    selectedRequest,
    onExecuteRequest,
    getCurrentEditorRequest,
    onRunCollection,
    onSetVariable,
    onCreateCollection,
    onCreateFolders,
    onCreateRequest,
    onMoveRequests,
    onGetLastResponse,
    onGenerateTests,
    onGeneratePreRequestScript,
    onUpdateRequest,
    getCollections,
    interactiveMode,
    extensionActions,
    fetchUrlTurnCountRef,
  });

  const streamTurn = useAiStreamTurn({ abortRef, streamFinishRef, setContextTokens });

  /**
   * `resume` is set only by the Retry button on a failed turn. The failed
   * attempt already rendered the user's bubble and already persisted the
   * history that ends with that user turn — re-sending the payload normally
   * would duplicate both. Resuming replays that exact snapshot instead, so a
   * retry is the same request, not a second one.
   */
  // Memoized: it is passed to a memoized ChatInput as onClearChat, and it is
  // called by sendMessage for `/clear` — so an unstable identity both defeats
  // that memo and makes sendMessage's dependency list a lie.
  const clearSession = useCallback(() => {
    abortRef.current = true;
    // Release any pending confirm gate + in-flight stream so the loop can unwind.
    if (confirmResolveRef.current) {
      const resolve = confirmResolveRef.current;
      confirmResolveRef.current = null;
      resolve(false);
    }
    streamFinishRef.current?.();
    setMessages([]);
    anthropicHistoryRef.current = [];
    setError(null);
    setIsStreaming(false);
    setContextTokens(0);
    // Clearing means gone, not "gone until the next launch" — and not "gone
    // until the panel is reopened" either.
    clearCachedConversation(currentWorkspaceId);
    if (currentWorkspaceId) clearConversation(currentWorkspaceId).catch(() => {});
  }, [currentWorkspaceId]);

  const sendMessage = useCallback(async (payload, { resume } = {}) => {
    const trimmed = (payload?.text || '').trim();
    const references = payload?.references || [];
    if (!trimmed) return;
    if (trimmed === '/clear') { clearSession(); return; }
    if (isStreaming) return;

    // The toolbar project rides in the message, not the cached system prompt (card 6.3).
    const apiContent = trimmed + buildReferenceContext(references) + projectContextLine();

    setError(null);
    setRetryable(null);
    setIsStreaming(true);
    abortRef.current = false;
    runDiagnosisUsedRef.current = false;
    fetchUrlTurnCountRef.current = 0;
    shouldAutoScrollRef.current = true;

    if (!resume) {
      setMessages((prev) => [...prev, { role: 'user', content: trimmed, toolCalls: [] }]);
      recordFeature(ACTION.AI_CHAT);   // identifier-free; a retry (resume) is not a new message
    }

    let currentMessages = resume
      ? [...resume.history]
      : [...anthropicHistoryRef.current, { role: 'user', content: apiContent }];
    inFlightHistoryRef.current = currentMessages;
    threadRef.current = { ...threadRef.current, token: persistToken() };
    // This turn belongs to THAT thread. A workspace switch replaces the
    // identity while the instance stays mounted, so after every await the
    // loop checks it and leaves silently — no bubble, no history, no persist
    // into whichever workspace is showing now.
    const thread = threadRef.current;
    const stale = () => threadRef.current !== thread;

    try {
      const chainContext = { collectionId: null, folderId: null };
      // Guards a runaway loop of pure-text max_tokens continuations (reset on any
      // tool progress). Long agentic work makes tool calls, which reset this.
      let textContinuations = 0;
      const MAX_TEXT_CONTINUATIONS = 6;

      while (true) {
        if (abortRef.current) break;

        // Streaming placeholder bubble
        setMessages((prev) => [...prev, { role: 'assistant', content: '', toolCalls: [], isStreaming: true }]);

        const { textContent, toolUseBlocks, stopReason } = await streamTurn(
          currentMessages,
          (delta) => {
            if (abortRef.current || stale()) return;
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [...prev.slice(0, -1), { ...last, content: last.content + delta, isStreaming: true }];
              }
              return prev;
            });
          },
        );

        if (stale()) return;

        // Interrupted (Stop button): finalize the streaming bubble keeping any
        // partial text, and persist a tool_use-free history entry so the next
        // message still has valid context (an assistant turn ending in an
        // unanswered tool_use would break the next API call). Then stop.
        if (abortRef.current) {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && last.isStreaming) {
              if (!last.content && !(last.toolCalls || []).length) return prev.slice(0, -1);
              return [...prev.slice(0, -1), { ...last, content: textContent || last.content, isStreaming: false }];
            }
            return prev;
          });
          anthropicHistoryRef.current = textContent
            ? [...currentMessages, { role: 'assistant', content: textContent }]
            : currentMessages;
          break;
        }

        // Build Anthropic-format assistant content for history
        const assistantContent = [];
        if (textContent) assistantContent.push({ type: 'text', text: textContent });
        for (const tb of toolUseBlocks) {
          assistantContent.push({ type: 'tool_use', id: tb.id, name: tb.name, input: tb.input });
        }
        currentMessages = [
          ...currentMessages,
          { role: 'assistant', content: assistantContent.length ? assistantContent : textContent },
        ];

        // No tool calls this turn (pure text). If the model was truncated at
        // max_tokens mid-thought (e.g. writing long content) without emitting a
        // tool call, nudge it to continue instead of abandoning the work after a
        // single intro line. Otherwise (end_turn / stop) finalize and stop.
        if (toolUseBlocks.length === 0) {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant') {
              return [...prev.slice(0, -1), { ...last, content: textContent, toolCalls: [], isStreaming: false }];
            }
            return prev;
          });
          anthropicHistoryRef.current = currentMessages;
          if (stopReason === 'max_tokens' && !abortRef.current && textContinuations < MAX_TEXT_CONTINUATIONS) {
            textContinuations += 1;
            currentMessages = [...currentMessages, { role: 'user', content: 'Continue.' }];
            inFlightHistoryRef.current = currentMessages;
            continue;
          }
          break;
        }

        // Tool calls completed this turn — run them even if the turn was cut off
        // at max_tokens (only fully-received tool_use blocks reach us). Making
        // progress resets the pure-text continuation guard.
        textContinuations = 0;

        // Show tool cards immediately, then execute. Confirmable actions with
        // Auto-run off render an approval card (CONFIRM_PENDING) instead of a
        // running spinner until the user clicks Confirm.
        const pendingToolCalls = toolUseBlocks.map((tb) => {
          const needsConfirm = toolNeedsConfirm(tb.name, tb.input, autoRunModeRef.current);
          return {
            id: tb.id,
            name: tb.name,
            input: tb.input,
            result: needsConfirm ? CONFIRM_PENDING : null,
            awaitingConfirm: false,
            interactive: interactiveMode && INTERACTIVE_UI_TOOLS.has(tb.name),
          };
        });
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return [...prev.slice(0, -1), { ...last, content: textContent, toolCalls: pendingToolCalls, isStreaming: true }];
          }
          return prev;
        });

        const patchToolCall = (i, patch) => setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            const updatedCalls = [...(last.toolCalls || [])];
            if (updatedCalls[i]) updatedCalls[i] = { ...updatedCalls[i], ...patch };
            return [...prev.slice(0, -1), { ...last, toolCalls: updatedCalls }];
          }
          return prev;
        });

        const uiToolCalls = [];
        for (let i = 0; i < toolUseBlocks.length; i++) {
          const tb = toolUseBlocks[i];
          const resolvedInput = resolveChainedToolInput(tb.name, tb.input, chainContext);

          let result;
          const needsConfirm = toolNeedsConfirm(tb.name, tb.input, autoRunModeRef.current);
          if (needsConfirm) {
            patchToolCall(i, { awaitingConfirm: true, result: CONFIRM_PENDING });
            const approved = await new Promise((resolve) => { confirmResolveRef.current = resolve; });
            confirmResolveRef.current = null;
            if (abortRef.current || stale()) return;
            patchToolCall(i, { awaitingConfirm: false, result: approved ? null : { cancelled: true } });
            result = approved ? await executeTool(tb.name, resolvedInput) : { cancelled: true };
          } else {
            result = await executeTool(tb.name, resolvedInput);
          }
          if (stale()) return;

          updateChainContext(tb.name, result, chainContext);
          uiToolCalls.push({
            id: tb.id,
            name: tb.name,
            input: resolvedInput,
            result,
            interactive: interactiveMode && INTERACTIVE_UI_TOOLS.has(tb.name),
          });
          patchToolCall(i, { result, awaitingConfirm: false });
        }

        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return [...prev.slice(0, -1), { ...last, content: textContent, toolCalls: uiToolCalls, isStreaming: false }];
          }
          return prev;
        });

        const hasTerminalTool = uiToolCalls.some((tc) => TERMINAL_TOOLS.has(tc.name));

        // P2: a failing run_collection earns ONE auto-diagnosis turn even with
        // Auto-run off — feed the enriched `failures` back so Claude can explain
        // and optionally propose an update_request fix. Guarded to run at most once.
        const failingRun = uiToolCalls.some(
          (tc) => tc.name === 'run_collection' && !tc.result?.error && (tc.result?.failed_count || 0) > 0
        );
        const wantDiagnosisTurn = failingRun && !runDiagnosisUsedRef.current;
        if (wantDiagnosisTurn) runDiagnosisUsedRef.current = true;

        // Auto-run OFF keeps the legacy fast-path: after a terminal action the
        // results are in the UI, so we skip Claude's summary turn. Auto-run ON is
        // agentic — feed condensed results back and let Claude continue chaining
        // (run a task then mark it done, run several tasks in order, etc.).
        if (hasTerminalTool && !autoRunModeRef.current && !wantDiagnosisTurn) {
          anthropicHistoryRef.current = [
            ...anthropicHistoryRef.current,
            { role: 'user', content: apiContent },
            { role: 'assistant', content: buildToolTurnHistoryEntry(textContent, uiToolCalls) },
          ];
          break;
        }

        // Lookup tools (get_collections, etc.) — feed condensed results back and continue.
        const toolResults = uiToolCalls.map((tc) => ({
          type: 'tool_result',
          tool_use_id: tc.id,
          content: condenseToolResultForApi(tc.name, tc.result, tc.input),
        }));
        currentMessages = [...currentMessages, { role: 'user', content: toolResults }];
        inFlightHistoryRef.current = currentMessages;
      }
    } catch (err) {
      if (stale()) return;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.isStreaming) {
          if (!last.content) {
            return prev.slice(0, -1);
          }
          // Partial content arrived before the error — clear the streaming cursor
          // so the bubble is finalized rather than frozen with an active indicator.
          return [...prev.slice(0, -1), { ...last, isStreaming: false }];
        }
        return prev;
      });
      // COMMS-15: without this, anthropicHistoryRef is left exactly as it was
      // before this turn started — the visible transcript still shows the
      // user's message (and any partial assistant text), but the AI silently
      // "forgets" this whole exchange on the next sendMessage call. Mirror the
      // abort branch above: currentMessages (which already includes the
      // user's turn and any completed prior tool rounds) is ALWAYS persisted,
      // even when nothing streamed before the error — only fold in whatever
      // text streamed as an extra assistant entry when there is some. We
      // never persist a bare tool_use block (streamTurn only reports
      // fully-received ones, so folding them as-is would leave the history
      // ending in an unanswered tool_use the next API call can't satisfy).
      const partialText = err?.partialTextContent;
      const hadPartialToolUse = (err?.partialToolUseBlocks || []).length > 0;
      anthropicHistoryRef.current = (partialText || hadPartialToolUse)
        ? [...currentMessages, { role: 'assistant', content: partialText || '(no response)' }]
        : currentMessages;
      setError(err.message || 'An error occurred');
      // `currentMessages` ends with this turn's user message (plus any tool
      // rounds that DID complete), so it is exactly the request that failed —
      // the right thing to replay. Provider 503s and rate limits are the
      // common case here and usually succeed on a second attempt.
      setRetryable({ payload: { text: trimmed, references }, history: currentMessages });
    } finally {
      inFlightHistoryRef.current = null;
      setIsStreaming(false);
    }
  }, [isStreaming, streamTurn, executeTool, interactiveMode, clearSession]);

  const retryLastTurn = useCallback(() => {
    if (!retryable || isStreaming) return;
    // Rewind the persisted history to the pre-failure snapshot first: a turn
    // that streamed partial text before dying appended it, and replaying on
    // top of that would ask the model to continue rather than to retry.
    anthropicHistoryRef.current = retryable.history;
    sendMessage(retryable.payload, { resume: retryable });
  }, [retryable, isStreaming, sendMessage]);

  const dismissError = useCallback(() => {
    setError(null);
    setRetryable(null);
  }, []);

  // Stop/interrupt the assistant mid-turn without clearing the chat. Aborts the
  // agentic loop, resolves any in-flight stream + pending confirm gate, and
  // finalizes the streaming bubble (keeping whatever text already arrived).
  const stopStreaming = useCallback(() => {
    abortRef.current = true;
    if (confirmResolveRef.current) {
      const resolve = confirmResolveRef.current;
      confirmResolveRef.current = null;
      resolve(false);
    }
    streamFinishRef.current?.();
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant' && last.isStreaming) {
        if (!last.content && !(last.toolCalls || []).length) return prev.slice(0, -1);
        return [...prev.slice(0, -1), { ...last, isStreaming: false }];
      }
      return prev;
    });
    setIsStreaming(false);
  }, []);

  useConversationPersistence({
    currentWorkspaceId,
    messages,
    isStreaming,
    setMessages,
    anthropicHistoryRef,
    inFlightHistoryRef,
    threadRef,
    confirmResolveRef,
    streamFinishRef,
  });

  // Resolve a pending Confirm/Cancel gate (buttons in the tool card).
  const handleToolConfirm = useCallback((approved) => {
    const resolve = confirmResolveRef.current;
    if (resolve) {
      confirmResolveRef.current = null;
      resolve(approved);
    }
  }, []);


  const whatIsSentText = buildWhatIsSentText(t, aiReadiness?.via);

  if (!isOpen) return null;

  return (
    <div
      className="flex flex-row h-full min-h-0 flex-shrink-0"
      style={{ width: panelWidth, minWidth: AI_PANEL_MIN_WIDTH, maxWidth: clampPanelWidth(panelWidth) }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize AI assistant panel"
        className={`resizer resizer--horizontal flex-shrink-0 self-stretch${isResizing ? ' dragging' : ''}`}
        style={{ cursor: 'col-resize' }}
        onMouseDown={handleResizeStart}
      />
      <div className="flex flex-col flex-1 min-w-0 bg-gray-50 dark:bg-zinc-900 border-l border-gray-200 dark:border-zinc-700 h-full overflow-hidden">
      <AiChatHeader
        interactiveMode={interactiveMode}
        setInteractiveMode={setInteractiveMode}
        autoRunMode={autoRunMode}
        setAutoRunMode={setAutoRunMode}
        aiReadResponses={aiReadResponses}
        updatePreference={updatePreference}
        clearSession={clearSession}
        closeLabel={closeLabel}
        onClose={onClose}
      />

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className={`ai-chat-messages flex flex-col w-full flex-1 min-h-0 px-3 py-3${
          messages.length === 0 && !error ? ' items-center justify-center' : ''
        }`}
      >
        {messages.length === 0 && !error && aiReadiness && aiReadiness.state !== 'ready' && (
          /* BYO-key first-run: no key / vault locked / no vault. Every user
             lands here once after the backend proxy retired — this state IS
             the migration UX, so it explains why and routes to setup. */
          <AiKeySetupState
            state={aiReadiness.state}
            onOpenSettings={() => {
              setSettingsInitialTab('aiChat');
              setSettingsModalOpen(true);
            }}
            onUnlockVault={() => openVaultGate('ai-chat')}
          />
        )}
        {messages.length === 0 && !error && (!aiReadiness || aiReadiness.state === 'ready') && (
          <div className="ai-chat-empty-state flex flex-col items-center text-center gap-4">
            <Ghost variant="classic" size={48} animated glow />
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Ask me anything</p>
              <p className="text-xs leading-relaxed text-gray-400 dark:text-zinc-500">
                I work across this whole workspace — requests, databases,
                <br />
                documents, boards, and your servers.
              </p>
              {/* Discovery, not decoration: the previous copy named three
                  capabilities out of forty, so nobody knew the rest existed. */}
              <AiStarterPrompts onPick={(text) => sendMessage({ text, references: [] })} />
              {/* The AI Act Art. 50(1) notice is NOT here: an empty-state line
                  disappears with the first message. It is the permanent
                  AiDisclosureLine under the composer. The 16.09.2026 removal
                  leaned on the "obvious" exception (the panel title and the
                  composer placeholder); the Commission reads that exception
                  restrictively, and this assistant acts as an agent whose
                  output lands in documents and boards, so the notice is back
                  and stays on — see aiChat/AiDisclosureLine.js. */}
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <MessageBubble key={idx} message={msg} onToolConfirm={handleToolConfirm} />
        ))}
        {/* Real class, not `dark:` utilities: index.css is a hand-rolled utility
            subset with no dark: variants, so the light `bg-red-50` was the only
            rule that landed and the banner stayed near-white in dark mode. */}
        {error && (
          <div className="ai-chat-error" role="alert">
            <div className="ai-chat-error-text">{error}</div>
            <div className="ai-chat-error-actions">
              {retryable && (
                <button
                  type="button"
                  className="ai-chat-error-btn"
                  onClick={retryLastTurn}
                  disabled={isStreaming}
                  data-testid="ai-error-retry"
                >
                  <RotateCcw size={11} />
                  Retry
                </button>
              )}
              <button type="button" className="ai-chat-error-btn" onClick={dismissError}>
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Standing "where is this going" line while the next turn goes through
          the Apiboo assistant (selected, or fallback for a keyless BYO
          provider). Its one-time first-use variant was removed 16.09.2026 -
          see aiChat/AiFallbackNotice.js for why this line is the disclosure. */}
      {aiReadiness?.via === 'apiboo' && (
        <AiFallbackNotice
          fallbackFrom={aiReadiness.fallbackFrom}
          selectedProvider={aiReadiness.selectedProvider}
          onOpenSettings={() => {
            setSettingsInitialTab('aiChat');
            setSettingsModalOpen(true);
          }}
        />
      )}

      <ChatInput
        onSend={sendMessage}
        onStop={stopStreaming}
        onClearChat={clearSession}
        isStreaming={isStreaming}
        isOpen={isOpen}
        collections={collections}
        environments={environments}
        extensionActions={extensionActions}
      />

      {/* AI Act Art. 50(1): permanent, in both modes, screen-reader readable. */}
      <AiDisclosureLine readiness={aiReadiness} />

      <AiChatStatusBar contextTokens={contextTokens} whatIsSentText={whatIsSentText} t={t} />
      </div>
    </div>
  );
}

export default AIChatPanel;
// Test seam: the last thing that runs before a tool result becomes prompt text.
export { condenseToolResultForApi, MessageBubble };
